import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type { SmallBody } from '../lib/objects';
import { useSmallBodies } from '../store/smallBodiesStore';
import { useScale, scaleVec3 } from '../store/scaleStore';
import { useSelection } from '../store/selectionStore';
import { useLayers } from '../store/layersStore';
import { useTimeline } from '../store/timelineStore';
import { cartesianAt, evolvedM } from '../lib/orbit';

const COLOR_BY_CLASS: Record<string, string> = {
  sednoid: '#c08cff',
  extreme_tno: '#ff8ad4',
  sedna_like_tno: '#a4c0ff',
  dwarf_planet: '#9fdcdc',
  unknown: '#888',
};

const RADIUS_BY_CLASS: Record<string, number> = {
  dwarf_planet: 0.12,
  sednoid: 0.10,
  extreme_tno: 0.08,
  sedna_like_tno: 0.08,
  unknown: 0.06,
};

interface Props {
  visible: boolean;
}

function bodyMatchesActiveLayers(
  body: SmallBody,
  active: { dwarfs: boolean; tnos: boolean; etnos: boolean },
): boolean {
  const cls = body.object_class;
  if (active.dwarfs && cls === 'dwarf_planet') return true;
  if (active.etnos && (cls === 'sednoid' || cls === 'extreme_tno')) return true;
  if (active.tnos && (
    cls === 'dwarf_planet' || cls === 'sednoid' ||
    cls === 'extreme_tno' || cls === 'sedna_like_tno'
  )) return true;
  return false;
}

/** Calcule la position héliocentrique d'un corps à un JD donné.
 *  - Si le corps est animable (a, e, i, Ω, ω, M tous connus), évolue M(t) selon Kepler 3
 *  - Sinon retourne sa position statique (provenant du backend, calculée à J2000)
 */
function bodyPositionAt(body: SmallBody, tJd: number): [number, number, number] {
  const e = body.elements;
  const canEvolve =
    !body.position_uncertain &&
    e.a_au !== null && e.a_au !== undefined &&
    e.e !== null && e.e !== undefined &&
    e.i_deg !== null && e.i_deg !== undefined &&
    e.Omega_deg !== null && e.Omega_deg !== undefined &&
    e.omega_deg !== null && e.omega_deg !== undefined &&
    e.M_deg !== null && e.M_deg !== undefined;

  if (canEvolve) {
    const Mt = evolvedM(e.M_deg!, e.a_au!, tJd);
    const v = cartesianAt(
      {
        a_au: e.a_au!,
        e: e.e!,
        i_deg: e.i_deg!,
        Omega_deg: e.Omega_deg!,
        omega_deg: e.omega_deg!,
      },
      Mt,
    );
    return [v[0], v[1], v[2]];
  }

  // Fallback : position statique fournie par le backend
  if (body.position_helio_au) {
    const p = body.position_helio_au;
    return [p[0], p[1], p[2]];
  }
  return [0, 0, 0];
}

function BodyMesh({
  body,
  hovered,
  onHover,
}: {
  body: SmallBody;
  hovered: string | null;
  onHover: (key: string | null) => void;
}) {
  const ref = useRef<THREE.Mesh>(null!);
  const mode = useScale((s) => s.mode);
  const isHovered = hovered === body.key;

  // Position initiale calculée AVANT le 1ᵉʳ render — pas de flash à l'origine
  const initialPos = useMemo<[number, number, number]>(() => {
    const tJd = useTimeline.getState().tJd;
    const [x, y, z] = bodyPositionAt(body, tJd);
    // Repère écliptique X,Y,Z → scène X, Z, -Y (Y up)
    return scaleVec3([x, z, -y], mode);
  }, [body, mode]);

  // Mise à jour live à chaque frame quand la timeline avance
  useFrame(() => {
    if (!ref.current) return;
    const tJd = useTimeline.getState().tJd;
    const m = useScale.getState().mode;
    const [x, y, z] = bodyPositionAt(body, tJd);
    const [sx, sy, sz] = scaleVec3([x, z, -y], m);
    ref.current.position.set(sx, sy, sz);
  });

  const color = COLOR_BY_CLASS[body.object_class] ?? COLOR_BY_CLASS.unknown;
  const radius = RADIUS_BY_CLASS[body.object_class] ?? RADIUS_BY_CLASS.unknown;

  return (
    <mesh
      ref={ref}
      position={initialPos}
      onPointerOver={(e) => {
        e.stopPropagation();
        onHover(body.key);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={() => {
        onHover(null);
        document.body.style.cursor = '';
      }}
      onClick={(e) => {
        e.stopPropagation();
        useSelection.getState().select({
          kind: 'small_body',
          id: body.key,
          name: body.name,
        });
      }}
    >
      <sphereGeometry args={[radius * (isHovered ? 1.5 : 1.0), 14, 10]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={isHovered ? 0.5 : 0.2}
      />
      {isHovered && (
        <Html
          center
          distanceFactor={10}
          style={{
            pointerEvents: 'none',
            background: 'rgba(11, 13, 20, 0.92)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: '8px 12px',
            color: '#e8ecf2',
            fontSize: 12,
            whiteSpace: 'nowrap',
            transform: 'translate(0, -28px)',
          }}
        >
          <strong>{body.name}</strong>
          <br />
          <span style={{ color: '#aab1bd' }}>
            {body.object_class.replace('_', ' ')} · a ≈ {body.elements.a_au?.toFixed(0)} UA
          </span>
        </Html>
      )}
    </mesh>
  );
}

export default function SmallBodies({ visible }: Props) {
  const bodies = useSmallBodies((s) => s.bodies);
  const load = useSmallBodies((s) => s.load);
  const active = useLayers((s) => s.active);
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    if (visible) void load();
  }, [visible, load]);

  const filtered = useMemo(
    () => bodies.filter(
      (b) => b.position_helio_au !== null &&
        bodyMatchesActiveLayers(b, active),
    ),
    [bodies, active],
  );

  if (!visible || filtered.length === 0) return null;

  return (
    <group>
      {filtered.map((b) => (
        <BodyMesh key={b.key} body={b} hovered={hovered} onHover={setHovered} />
      ))}
    </group>
  );
}
